/**
 * 회원 등록 React Query 훅 (instructor-web)
 *
 * 백엔드 `createInstructorMember` (requireCenterMembership)와 1:1 contract.
 * 강사는 자신이 소속된 센터에 회원을 등록할 수 있다.
 */

import { useMutation } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { getFirebaseFunctions } from "@/config/firebase";

// --- 백엔드 contract (firebase-functions/src/member/create-instructor-member.ts) ---

interface CreateInstructorMemberRequest {
  centerName: string;
  name: string;
  mobile: string;
}

interface CreateInstructorMemberResponse {
  success: true;
  /** 서버에서 발급된 memberId (8자리 이상, 10_000_000+) */
  memberId: number;
}

export interface CreateMemberParams {
  centerName: string;
  name: string;
  mobile: string;
}

/**
 * 회원 생성 mutation.
 *
 * 성공 시 서버에서 발급된 memberId를 반환한다. 목록 갱신은 호출자(모달의 onSuccess)가
 * 직접 refetch한다.
 */
export function useCreateMember() {
  return useMutation({
    mutationFn: async (params: CreateMemberParams) => {
      const fn = httpsCallable<CreateInstructorMemberRequest, CreateInstructorMemberResponse>(
        getFirebaseFunctions(),
        "createInstructorMember",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}

// --- 수정(강사 가능) / 삭제(owner·admin 전용) contract ---

interface UpdateMemberRequest {
  centerId: number;
  memberId: number;
  name?: string;
  mobile?: string;
}

interface DeleteMemberRequest {
  centerId: number;
  memberId: number;
}

interface MemberMutationResponse {
  success: true;
}

export type UpdateMemberParams = UpdateMemberRequest;
export type DeleteMemberParams = DeleteMemberRequest;

/**
 * 회원 정보 수정 mutation. 강사 전용 `updateInstructorMember`(requireAuth + 센터 멤버십)를
 * 호출하므로 강사도 호출 가능하다. centerId+memberId 필수, name·mobile 중 1개 이상.
 */
export function useUpdateMember() {
  return useMutation({
    mutationFn: async (params: UpdateMemberParams) => {
      const fn = httpsCallable<UpdateMemberRequest, MemberMutationResponse>(
        getFirebaseFunctions(),
        "updateInstructorMember",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}

/**
 * 회원 삭제(소프트) mutation. `deleteMember`(requireAdmin)를 호출하므로 owner/admin만
 * 성공한다. 호출 전 페이지에서 role로 분기한다.
 */
export function useDeleteMember() {
  return useMutation({
    mutationFn: async (params: DeleteMemberParams) => {
      const fn = httpsCallable<DeleteMemberRequest, MemberMutationResponse>(
        getFirebaseFunctions(),
        "deleteMember",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}
